import { PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import type { LocationId } from "./location.model";
import { assertUnpinnedReference } from "./spine.model";

/** Stable physical-device identity, independent of its FHIR locator. */
export type DeviceId = DomainId<"DeviceId">;

export function asDeviceId(value: string): DeviceId {
  if (!value) throw new PreconditionFailedError("A Device identity cannot be empty");
  return value as DeviceId;
}

// ============================================================
// Device — one physical unit of a DeviceDefinition
//
// An INSTANCE, not a definition: it gets no spine. There is no lineage to
// revise, no version to pin and no `availability` — a unit is in service or it
// is not, which is `status`, and that is a fact about this object rather than a
// decision about a catalog entry.
//
// A device is a RESOURCE in the scheduling sense: it holds calendars, so it can
// be booked. That is the whole reason it is a domain object rather than an
// inventory row.
//
// IT KNOWS NOTHING ABOUT THOSE CALENDARS. A Schedule is its own aggregate, and
// a `scheduleCount` here made the unit answer questions about another one — so
// every read of a device fetched its schedules, and "may I retire this?"
// depended on state this entity does not own. Registration opens the calendar
// and deregistration closes it, both in one session, so the two stay in step
// without either aggregate holding the other's facts.
//
// THE DEFINITION REFERENCE IS UNPINNED, for the same reason references inside
// the catalog are: this unit IS a Model X, and when the model's parameters are
// revised the unit is still that model. Pinning would freeze a physical object
// to a description of itself.
//
// DELETED, AND THE REMOVALS ARE LOAD-BEARING:
//
//   `type: CodeableConcept` — scheduling used to match free devices by a
//   category code on the unit. Once a kind exists it IS the type, and keeping
//   both leaves two answers to "what is this". Matching moves to
//   `Device.definition`.
//
//   `deviceNames[]` — an array of {name, type} mirroring FHIR's several naming
//   systems. With the model name on the kind, a unit needs one local human
//   label ("Laser 2", "the one in room 3"), so the "a device requires a name"
//   invariant goes with it: an unlabelled unit is identified by its kind and
//   its serial number, which is more than a name ever gave.
//
//   `manufacturer` / `modelNumber` — moved UP to the kind, where they are
//   stored once instead of on every unit of the same model.
//
//   `contact`, `notes`, `timezone` — no consumer in the redesigned model.
// ============================================================

/**
 * Whether the unit is in service. Not `availability`: that word belongs to a
 * catalog lineage, and conflating "this laser is in the shop" with "this
 * product model is withdrawn" is how a repair takes a whole kind out of the
 * catalog.
 */
export type DeviceStatus = "Active" | "Inactive" | "EnteredInError";

interface DeviceFields {
  /** Bare canonical url of the kind. Never `|version`. */
  definitionUrl: string;
  label?: string;
  description?: string;
  serialNumber?: string;
  distinctIdentifier?: string;
  locationId?: LocationId;
  status: DeviceStatus;
}

export interface CreateDeviceInput extends DeviceFields {
  id: string;
}

export interface DeviceFacts extends DeviceFields {
  id: DeviceId;
}

/** A stored unit may have no asserted service status. */
export type StoredDeviceProps = Omit<DeviceFacts, "status"> & { status?: DeviceStatus };

/** What may be corrected about the unit's manufacturer-issued identifiers. */
interface DeviceIdentifiers {
  serialNumber?: string;
  distinctIdentifier?: string;
}

export class Device {
  private state: DeviceFacts;

  private constructor(facts: DeviceFacts) {
    this.state = {
      ...structuredClone(facts),
      // A unit that is not a model of something cannot be matched to a
      // requirement, so it cannot be scheduled — which makes a missing kind a
      // broken device rather than an incomplete one. Refusing it here beats a
      // nullable field every scheduler has to remember to guard.
      definitionUrl: assertUnpinnedReference(facts.definitionUrl),
    };
  }

  static create(input: CreateDeviceInput): Device {
    return new Device({ ...structuredClone(input), id: asDeviceId(input.id) });
  }

  static restore(facts: StoredDeviceProps): Device {
    // A unit in inventory is in service until someone takes it out.
    return new Device({ ...facts, status: facts.status ?? "Active" });
  }

  get id(): DeviceId {
    return this.state.id;
  }

  get definitionUrl(): string {
    return this.state.definitionUrl;
  }

  get isActive(): boolean {
    return this.state.status === "Active";
  }

  get facts(): DeviceFacts {
    return structuredClone(this.state);
  }

  snapshot(): DeviceFacts {
    return this.facts;
  }

  /**
   * Re-point the unit at the kind it actually is. A correction of a mistaken
   * registration rather than a change to the equipment, so it is checked the
   * same way the constructor is: still a lineage, never a revision.
   */
  setDefinition(definitionUrl: string): void {
    this.state.definitionUrl = assertUnpinnedReference(definitionUrl);
  }

  /** The local human label. Optional, and clearing it is a legitimate act — hence the undefined. */
  setLabel(label: string | undefined): void {
    this.state.label = blankToUndefined(label);
  }

  /** Human-facing notes about this physical unit. */
  setDescription(description: string | undefined): void {
    this.state.description = blankToUndefined(description);
  }

  /** Siting. A unit with no location is unsited, not invalid — new stock has not been put anywhere yet. */
  setLocation(locationId: LocationId | undefined): void {
    this.state.locationId = locationId;
  }

  /**
   * The manufacturer's identifiers, recorded at registration and corrected when
   * they were mistyped. One command rather than two setters because they are
   * read off the same label at the same moment.
   */
  setIdentifiers(identifiers: DeviceIdentifiers): void {
    if ("serialNumber" in identifiers) this.state.serialNumber = blankToUndefined(identifiers.serialNumber);
    if ("distinctIdentifier" in identifiers) {
      this.state.distinctIdentifier = blankToUndefined(identifiers.distinctIdentifier);
    }
  }

  /**
   * Take it out of service. Closing the calendar is the caller's other half of
   * the same session — whether it MAY close is Schedule's own rule, since only
   * that aggregate knows what is booked on it.
   */
  deregister(): void {
    this.state.status = "Inactive";
  }

  setStatus(status: DeviceStatus): void {
    if (status === "Inactive") {
      this.deregister();
      return;
    }
    this.state.status = status;
  }
}

function blankToUndefined(value: string | undefined): string | undefined {
  return value === undefined || value.trim() === "" ? undefined : value;
}
